import { useState } from 'react';

function ContactModal({ onClose }: { onClose: () => void }) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const data = new URLSearchParams(new FormData(e.currentTarget) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d7pj49el0bai2p3ha1b0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-900 transition-colors cursor-pointer">
          <i className="ri-close-line text-xl"></i>
        </button>
        {submitted ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 flex items-center justify-center bg-amber-50 rounded-full mx-auto mb-4">
              <i className="ri-check-line text-3xl text-amber-600"></i>
            </div>
            <h3 className="text-xl font-light text-gray-900 mb-2">Message Sent</h3>
            <p className="text-sm text-gray-500 font-light">A member of our partner team will be in touch within 24 hours.</p>
            <button onClick={onClose} className="mt-6 px-6 py-2.5 bg-[#1a2b3c] text-white text-sm font-light rounded-full hover:bg-[#243447] border border-amber-400/40 hover:border-amber-400/70 transition-colors cursor-pointer whitespace-nowrap">Close</button>
          </div>
        ) : (
          <>
            <h3 className="text-xl font-light text-gray-900 mb-1">
              Contact Us
            </h3>
            <p className="text-sm text-gray-400 font-light mb-6">
              Tell us about your property and we&apos;ll get back to you shortly.
            </p>
            <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-light text-gray-500 uppercase tracking-widest mb-1.5">First Name *</label>
                  <input type="text" name="first_name" required placeholder="John" className="w-full px-4 py-2.5 text-sm font-light border border-gray-200 rounded-lg focus:outline-none focus:border-amber-500 transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-light text-gray-500 uppercase tracking-widest mb-1.5">Last Name *</label>
                  <input type="text" name="last_name" required placeholder="Smith" className="w-full px-4 py-2.5 text-sm font-light border border-gray-200 rounded-lg focus:outline-none focus:border-amber-500 transition-colors" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-light text-gray-500 uppercase tracking-widest mb-1.5">Business Email *</label>
                <input type="email" name="email" required placeholder="john@hotel.com" className="w-full px-4 py-2.5 text-sm font-light border border-gray-200 rounded-lg focus:outline-none focus:border-amber-500 transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-light text-gray-500 uppercase tracking-widest mb-1.5">Phone</label>
                <input type="tel" name="phone" placeholder="(321) 000-0000" className="w-full px-4 py-2.5 text-sm font-light border border-gray-200 rounded-lg focus:outline-none focus:border-amber-500 transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-light text-gray-500 uppercase tracking-widest mb-1.5">Property Name</label>
                <input type="text" name="property_name" placeholder="Hotel / Property Name" className="w-full px-4 py-2.5 text-sm font-light border border-gray-200 rounded-lg focus:outline-none focus:border-amber-500 transition-colors" />
              </div>
              <button type="submit" disabled={submitting} className="w-full py-3 bg-[#1a2b3c] text-white text-sm font-light rounded-full hover:bg-[#243447] border border-amber-400/40 hover:border-amber-400/70 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
                {submitting ? 'Submitting...' : 'Send Message'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default function FinalCTA() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <section className="relative py-28 overflow-hidden bg-gray-950">
        {/* Background texture */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }}></div>

        {/* Ambient glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-amber-600/10 blur-3xl pointer-events-none"></div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
          {/* Pre-label */}
          <p className="text-xs font-light text-amber-500 uppercase tracking-widest mb-6" style={{ letterSpacing: '0.3em' }}>
            Ready to Partner?
          </p>

          {/* Headline */}
          <h2
            className="text-5xl lg:text-6xl font-extralight text-white leading-tight mb-6"
          >
            Your Hotel's Best Quarter Starts with<br />
            <span className="text-amber-400 italic">One Conversation.</span>
          </h2>

          {/* Body */}
          <p className="text-base font-light text-gray-400 leading-relaxed mb-10 max-w-2xl mx-auto">
            No forms buried in a pipeline. No waiting weeks for a proposal. Just a direct conversation with a senior 360 partner about what our Hotel Management Service, Hotel Revenue Management Service, and Hotel Brand Management Service could do for your property — this week.
          </p>

          {/* Trust checkmarks */}
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 mb-12">
            {[
              'No commitment required',
              'Response within 24 business hours',
              'Senior partner — not a junior rep',
              'Tailored to your specific property',
            ].map((item, i) => (
              <div key={i} className="flex items-center space-x-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-check-line text-amber-500 text-sm"></i>
                </div>
                <span className="text-sm font-light text-gray-400 whitespace-nowrap">{item}</span>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => setModalOpen(true)}
              className="group inline-flex items-center space-x-2 px-6 py-3 bg-[#1a2b3c] text-white text-sm font-light rounded-full hover:bg-[#243447] border border-amber-400/40 hover:border-amber-400/70 transition-all cursor-pointer whitespace-nowrap"
            >
              <span>Contact Us</span>
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </button>
          </div>

          {/* Contact nudge */}
          <p className="mt-10 text-sm font-light text-gray-600">
            Prefer to call directly?{' '}
            <a href="tel:321-336-8093" className="text-amber-500 hover:text-amber-400 transition-colors">
              321-336-8093
            </a>
            {' '}—{' '}
            <a href="mailto:info@360hospitalitygroup.com" className="text-amber-500 hover:text-amber-400 transition-colors">
              info@360hospitalitygroup.com
            </a>
          </p>
        </div>
      </section>

      {modalOpen && <ContactModal onClose={() => setModalOpen(false)} />}
    </>
  );
}
